/**
 * Google Gemini SDK adapter for browser automation tool
 */

import type { FunctionDeclaration, FunctionDeclarationSchema } from '@google/generative-ai';
import { SchemaType } from '@google/generative-ai';
import { executeBrowserTask } from './core.js';
import type {
  BrowserConfig,
  BrowserTaskInput,
  BrowserTaskResult,
} from './types.js';
import { BROWSER_TOOL_DESCRIPTION, BROWSER_SYSTEM_PROMPT } from './prompts.js';

/**
 * Parameter schema for the browser tool (Gemini format)
 */
const TOOL_PARAMETERS: FunctionDeclarationSchema = {
  type: SchemaType.OBJECT,
  properties: {
    task: {
      type: SchemaType.STRING,
      description: 'Natural language description of what to do (e.g., "Test checkout flow for buying a pineapple")',
    },
    url: {
      type: SchemaType.STRING,
      description: 'Starting URL (e.g., https://3000-xyz.e2b.dev). Required if navigating to a specific page.',
    },
    maxSteps: {
      type: SchemaType.NUMBER,
      description: 'Maximum number of browser actions to take (1-50). Default: 10. Use 15-30 for complex flows.',
    },
    region: {
      type: SchemaType.STRING,
      description: 'Browserless region: sfo (US West Coast) or lon (Europe). Default: sfo.',
    },
  },
  required: ['task'],
};

/**
 * Gemini-native browser function declaration
 * 
 * @example
 * ```typescript
 * import { GoogleGenerativeAI } from '@google/generative-ai';
 * import { browserFunctionDeclaration, execute } from '@morphllm/morphsdk/tools/browser/gemini';
 * 
 * const genAI = new GoogleGenerativeAI(process.env.GOOGLE_API_KEY);
 * const model = genAI.getGenerativeModel({
 *   model: 'gemini-3-flash-preview',
 *   tools: [{ functionDeclarations: [browserFunctionDeclaration] }]
 * });
 * 
 * const chat = model.startChat();
 * const result = await chat.sendMessage('Test the checkout flow');
 * 
 * // Handle function call
 * const call = result.response.functionCalls()?.[0];
 * if (call) {
 *   const taskResult = await execute(call.args, { apiKey: 'key' });
 *   console.log(taskResult);
 * }
 * ```
 */
export const browserFunctionDeclaration: FunctionDeclaration = {
  name: 'browser_task',
  description: BROWSER_TOOL_DESCRIPTION,
  parameters: TOOL_PARAMETERS,
};

/**
 * Execute browser task
 * 
 * @param input - Tool input with task and optional url/maxSteps
 * @param config - Configuration with apiKey
 * @returns Task execution result
 */
export async function execute(
  input: BrowserTaskInput | string,
  config?: BrowserConfig
): Promise<BrowserTaskResult> {
  const parsedInput = typeof input === 'string' ? JSON.parse(input) : input;
  return executeBrowserTask(parsedInput, config);
}

/**
 * Format browser task result for Gemini tool result
 * 
 * Returns a concise summary suitable for agent context. The full result object
 * (with urls, errors, action_history, judgement, etc.) is available when calling
 * execute() directly, but this formatted string omits those details to save tokens.
 * 
 * @param result - Browser task result with full history data
 * @returns Formatted string summary for tool result
 */
export function formatResult(result: BrowserTaskResult): string {
  if (result.success) {
    const parts = [
      '✅ Browser task completed successfully',
      `Steps taken: ${result.stepsTaken ?? 0}`,
      result.executionTimeMs ? `Execution time: ${result.executionTimeMs}ms` : null,
      '',
      'Result:',
      result.result || 'Task completed',
    ];
    return parts.filter(Boolean).join('\n');
  }

  return `❌ Browser task failed: ${result.error || 'Unknown error'}`;
}

/**
 * Get system prompt for browser automation
 */
export function getSystemPrompt(): string {
  return BROWSER_SYSTEM_PROMPT;
}

/**
 * Gemini tool with execute method attached
 */
export interface GeminiBrowserTool extends FunctionDeclaration {
  execute: (input: BrowserTaskInput | string) => Promise<BrowserTaskResult>;
  formatResult: (result: BrowserTaskResult) => string;
  getSystemPrompt: () => string;
}

/**
 * Create a configured browser tool with execute and formatResult methods
 * 
 * @param config - Browser worker configuration
 * @returns Function declaration with execute and formatResult methods
 * 
 * @example
 * ```typescript
 * import { GoogleGenerativeAI, FunctionCallingMode } from '@google/generative-ai';
 * import { createBrowserTool } from '@morphllm/morphsdk/tools/browser/gemini';
 * 
 * const tool = createBrowserTool({ apiKey: process.env.MORPH_API_KEY });
 * 
 * const genAI = new GoogleGenerativeAI(process.env.GOOGLE_API_KEY);
 * const model = genAI.getGenerativeModel({
 *   model: 'gemini-3-flash-preview',
 *   tools: [{ functionDeclarations: [tool] }],
 *   toolConfig: { functionCallingConfig: { mode: FunctionCallingMode.AUTO } }
 * });
 * 
 * const chat = model.startChat();
 * const result = await chat.sendMessage('Test the checkout flow at https://example.com');
 * 
 * // Handle function call
 * const call = result.response.functionCalls()?.[0];
 * if (call && call.name === tool.name) {
 *   const taskResult = await tool.execute(call.args);
 *   console.log(tool.formatResult(taskResult));
 *   
 *   // Send result back to model
 *   await chat.sendMessage([{
 *     functionResponse: {
 *       name: call.name,
 *       response: { result: tool.formatResult(taskResult) }
 *     }
 *   }]);
 * }
 * ```
 */
export function createBrowserTool(config?: BrowserConfig): GeminiBrowserTool {
  const declaration: FunctionDeclaration = {
    name: 'browser_task',
    description: BROWSER_TOOL_DESCRIPTION,
    parameters: TOOL_PARAMETERS,
  };

  return Object.assign(declaration, {
    execute: async (input: BrowserTaskInput | string): Promise<BrowserTaskResult> => {
      return execute(input, config);
    },
    formatResult: (result: BrowserTaskResult): string => {
      return formatResult(result);
    },
    getSystemPrompt: (): string => {
      return getSystemPrompt();
    },
  });
}

// Legacy alias for consistency with some patterns
export const browserTool = browserFunctionDeclaration;

export default browserFunctionDeclaration;



